import { ReactNode } from 'react';
import { UI_ORDER_TYPES } from '@velocity-exchange/common';
import { notify, NotificationProps } from 'src/utils/notifications';
import { FeePayerError } from 'src/constants/errors/FeePayerError';
// Type-only: this module is headless, so it must not pull the store into its
// runtime dependency graph (it is also re-exported by toastHelpers).
import type { VelocityStore } from 'src/stores/VelocityStore/useVelocityStore';

/**
 * Shared "order-action toast lifecycle" for every non-market order flow
 * (cancel / edit / open non-market / TP-SL / close-multiple / spot non-market / swap).
 *
 * Every such hook used to hand-roll the same sequence inline:
 *
 *   1. show an "awaiting" toast (`awaiting` when auto-signing, otherwise
 *      `awaiting_wallet_confirmation`), pinned with `showUntilCancelled`
 *   2. run the transaction work
 *   3. update the same toast to a terminal `success` (or `warning`/`error`) toast
 *   4. on failure: re-throw `FeePayerError` (so the auto-sign fallback can retry),
 *      otherwise route the error to the tx-error handler
 *
 * This module owns steps 1, 3 and 4 (including the `updatePrevious` mechanics and
 * the success copy), leaving callers to supply only the work and the copy. Callers
 * stay in control of behaviour that genuinely differs between flows via a few
 * extension points (custom pending props, an outcome-returning `success`
 * function, and their own `handleError`) — no per-flow special cases live
 * in here.
 *
 * It is deliberately headless (no React) so it can be unit-tested by asserting on
 * `useNotificationStore` directly.
 */

/** Descriptor used to derive standard "…order placed/edited." success copy. */
export type OrderToastCopyDescriptor = {
	orderType: VelocityStore['tradeForm']['orderType'];
	editingOrder?: boolean;
	numOrders?: number;
};

/**
 * Standard order success copy, e.g. "Limit order placed." / "Stop orders edited.".
 * Lives here so the module fully owns terminal success copy; re-exported from
 * `VelocityStore/helpers/toastHelpers` for backwards compatibility.
 */
export function getOrderToastMessage({
	orderType,
	editingOrder,
	numOrders,
}: OrderToastCopyDescriptor): string {
	return `${UI_ORDER_TYPES[orderType].label} order${
		numOrders && numOrders > 1 ? 's' : ''
	} ${editingOrder ? 'edited' : 'placed'}.`;
}

/** Terminal toast the work resolves to. `none` shows no terminal toast. */
export type OrderActionToastOutcome =
	| {
			type: 'success' | 'warning' | 'error';
			message: ReactNode;
			description?: ReactNode;
			lengthMs?: number;
	  }
	| { type: 'none' };

/**
 * How to build the terminal toast once the work resolves. Either explicit copy,
 * a descriptor for the standard order copy, or a function of the work's result
 * (used when the terminal type/copy depends on the outcome — e.g. a partially
 * failed batch → `warning`, or a null tx sig → `error`).
 */
export type OrderActionSuccess<T> =
	| { message: ReactNode; description?: ReactNode; lengthMs?: number }
	| { order: OrderToastCopyDescriptor }
	| ((result: T) => OrderActionToastOutcome);

export interface OrderActionToastParams<T> {
	toastId: string;
	/** Auto-signing shows an `awaiting` toast with `pendingMessage`; manual signing shows `awaiting_wallet_confirmation` with the store's built-in copy. */
	isAutoSigned?: boolean;
	/** Copy for the awaiting toast — only shown when `isAutoSigned` is true. */
	pendingMessage?: string;
	/** Extra props merged into the pending toast (escape hatch, e.g. swap's timeout/override flags). */
	pendingToastProps?: Partial<NotificationProps>;
	/** The transaction work. Runs after the pending toast; its result is passed to `success` and returned to the caller. */
	run: () => Promise<T>;
	/** Terminal toast copy / outcome. */
	success: OrderActionSuccess<T>;
	/** Routes a non-`FeePayerError` failure to the caller's tx-error handler (may run its own interception first, e.g. tx-too-large). */
	handleError: (error: unknown, toastId: string) => void;
	/** When set, logs `console.error(errorLogLabel, error)` before handling — preserves each hook's existing log line. */
	errorLogLabel?: string;
}

function resolveOutcome<T>(
	success: OrderActionSuccess<T>,
	result: T
): OrderActionToastOutcome {
	if (typeof success === 'function') {
		return success(result);
	}
	if ('order' in success) {
		return { type: 'success', message: getOrderToastMessage(success.order) };
	}
	return {
		type: 'success',
		message: success.message,
		description: success.description,
		lengthMs: success.lengthMs,
	};
}

/**
 * Runs the awaiting → work → terminal toast lifecycle described above.
 *
 * Resolves to the work's result, or `undefined` when a (non-rethrown) error was
 * routed to `handleError`. Re-throws `FeePayerError` (unless disabled) so the
 * auto-sign fallback in `useAutoSignVelocityActionWithFallback` can retry.
 */
export async function runOrderActionToast<T>({
	toastId,
	isAutoSigned = false,
	pendingMessage,
	pendingToastProps,
	run,
	success,
	handleError,
	errorLogLabel,
}: OrderActionToastParams<T>): Promise<T | undefined> {
	notify({
		id: toastId,
		type: isAutoSigned ? 'awaiting' : 'awaiting_wallet_confirmation',
		message: isAutoSigned ? pendingMessage : undefined,
		showUntilCancelled: true,
		...pendingToastProps,
	});

	try {
		const result = await run();

		const outcome = resolveOutcome(success, result);
		if (outcome.type !== 'none') {
			notify({
				id: toastId,
				type: outcome.type,
				message: outcome.message,
				updatePrevious: true,
				// Omit `description`/`lengthMs` when unset so `notify`'s defaults
				// apply (description defaults to '' so a prior description clears).
				...(outcome.description !== undefined
					? { description: outcome.description }
					: {}),
				...(outcome.lengthMs !== undefined
					? { lengthMs: outcome.lengthMs }
					: {}),
			});
		}

		return result;
	} catch (error) {
		if (errorLogLabel) {
			console.error(errorLogLabel, error);
		}

		if (error instanceof FeePayerError) {
			// handled by useAutoSignVelocityActionWithFallback (retries manually)
			throw error;
		}

		handleError(error, toastId);
		return undefined;
	}
}
